import React from "react";
import RenderSectionTitle from "./RenderSectionTitle";
import RenderSubSectionTitle from "./RenderSubSectionTitle";
import _ from "lodash";
import {
  getEntitiesFor,
  groupEntitiesBySubsection,
  getSubsectionsOf,
} from "./DataFlowHelper";
import { bracketed, fixMedicineInsn, fixMedicineName } from "../../util/util";

function getSubSectionStyle(relevantEntities) {
  return {
    width: `${
      relevantEntities[0].subsectionName === "CASE_HISTORY" ? "90vw" : "47vw"
    }`,
    marginRight: "10px",
  };
}

const mergeImagingLabs = (es) => {
  es = _.cloneDeep(es);
  const __imgLabs = _.filter(es, (x) => x.entityName === "IMAGING/LABS");
  if (__imgLabs.length === 0) {
    return es;
  }
  let first = _.first(__imgLabs); //__imgLabs[0];
  let imageLabsEntity = first;
  const newInsn = __imgLabs
    .map((x) => x.instructions + bracketed(x.notes))
    .join(", ");
  imageLabsEntity.instructions = newInsn;
  imageLabsEntity.notes = "";
  imageLabsEntity.final = true;
  const ess = _.filter(es, (x) => x.entityName !== "IMAGING/LABS");
  return [...ess, imageLabsEntity];
};

function PlanSection({
  entities,
  sectionMaps,
  subSectionMaps,
  styleOptions,
  isPediatricPatient,
}) {
  let sectionEntities = getEntitiesFor(entities, "PLAN");
  if (sectionEntities.length === 0) return null;
  sectionEntities = mergeImagingLabs(sectionEntities);
  const PLAN_SubSectionList = getSubsectionsOf("PLAN");
  const groupedEntities = groupEntitiesBySubsection(
    sectionEntities,
    PLAN_SubSectionList
  );
  const sectionTitleRender = sectionMaps["PLAN"];
  return (
    <div>
      <RenderSectionTitle text={sectionTitleRender} />
      <div style={styleOptions.mainContainerStyle}>
        {groupedEntities.map((subsection) => {
          let relevantEntities = subsection.entities;
          if (!relevantEntities.length) return null;
          const subsectionTitleRender =
            subSectionMaps["PLAN"][subsection.subsectionName];
          console.dir({ relevantEntities });
          if (
            isPediatricPatient &&
            subsection.subsectionName === "NEXT_STEPS"
          ) {
            const template = _.last(relevantEntities);
            relevantEntities.push({
              ...template,
              entityType: "NEXT_STEPS",
              entityName: "IMMUNIZATION / VACCINATION STATUS",
              notes: "YES | NO ——— If No, then refer to Paediatrician",
              instructions: undefined,
            });
            relevantEntities.push({
              ...template,
              entityType: "NEXT_STEPS",
              entityName: "OVERALL NUTRITIONAL STATUS",
              notes:
                "GOOD | AVERAGE | POOR ——— If Poor, then refer to Paediatrician",
              instructions: undefined,
            });
            relevantEntities.push({
              ...template,
              entityType: "NEXT_STEPS",
              entityName: "NOTES",
              instructions:
                "Kindly refer to the Immunization Folder for more details.",
            });
          }
          return (
            <div style={getSubSectionStyle(relevantEntities)}>
              <RenderSubSectionTitle text={subsectionTitleRender} />
              {relevantEntities.map((entity, index) => {
                let elements = [];
                let firstLine = [];
                firstLine.push(<>{index + 1}. </>);
                if (entity.laterality && entity.organ === "EYE") {
                  if (entity.laterality === "LEFT") {
                    firstLine.push(<b>LE - </b>);
                  } else if (entity.laterality === "RIGHT") {
                    firstLine.push(<b>RE - </b>);
                  } else {
                    firstLine.push(<b>BE - </b>);
                  }
                }
                firstLine.push(
                  <b style={styleOptions.bulletPointTitleStyle}>
                    {fixMedicineName(entity.entityName)}
                  </b>
                );
                if (entity.notes) {
                  firstLine.push(<> ({entity.notes})</>);
                }
                if (entity.molecule) {
                  elements.push(<> ({entity.molecule}) </>);
                }
                if (entity.instructions) {
                  let text = <></>;
                  const processInstructions = (instructions) => {
                    return instructions
                      .split(/[;,]/)
                      .map((instruction, index) => {
                        const words = instruction.trim().split(/\s+/);
                        return (
                          <div key={index}>
                            {words.map((word, wordIndex) => {
                              const lowerWord = word.toLowerCase();
                              const prevWord =
                                wordIndex > 0
                                  ? words[wordIndex - 1].toLowerCase()
                                  : "";
                              const nextWord =
                                wordIndex < words.length - 1
                                  ? words[wordIndex + 1].toLowerCase()
                                  : "";

                              if (
                                ["left", "right", "both"].includes(lowerWord) &&
                                (nextWord === "eye" ||
                                  nextWord === "eyes" ||
                                  prevWord === "eye" ||
                                  prevWord === "eyes")
                              ) {
                                return <strong key={wordIndex}>{word} </strong>;
                              } else if (
                                (lowerWord === "eye" || lowerWord === "eyes") &&
                                (["left", "right", "both"].includes(prevWord) ||
                                  ["left", "right", "both"].includes(nextWord))
                              ) {
                                return <strong key={wordIndex}>{word} </strong>;
                              } else if (
                                lowerWord === "eye" ||
                                lowerWord === "eyes"
                              ) {
                                return <strong key={wordIndex}>{word} </strong>;
                              } else {
                                return `${word} `;
                              }
                            })}
                          </div>
                        );
                      });
                  };

                  if (entity.subsectionName === "MEDICATIONS") {
                    text = processInstructions(entity.instructions);
                  } else {
                    text = processInstructions(entity.instructions);
                  }
                  elements.push(text);
                }

                if (entity.anaesthesia) {
                  let niceStrings = {
                    TOPICAL_ANAESTHESIA: "TA",
                    LOCAL_ANAESTHESIA: "LA",
                    REGIONAL_ANAESTHESIA: "RA",
                    GENERAL_ANAESTHESIA: "GA",
                  };
                  elements.push(<>↓: {niceStrings[entity.anaesthesia]} </>);
                }
                if (entity.sxLinkedInvestigations) {
                  let niceStrings = {
                    BIOMETRY_OCT_SPECULAR: "Biometry, OCT and Specular",
                    BIOMETRY_OCT_SYRINGING_SPECULAR:
                      "Biometry, OCT, Syringing and Specular",
                    OCT_DFP: "OCT and DFP",
                    "ECG_X-RAY_PPBS": "ECG, X-Ray and PPBS",
                  };
                  elements.push(" | Pre-Surgery Investigations: ");
                  elements.push(niceStrings[entity.sxLinkedInvestigations]);
                  if (entity.notes) {
                    elements.push(` (${entity.notes})`);
                  }
                }

                return (
                  <div style={styleOptions.subSectionBulletPoint}>
                    <div>
                      {firstLine.map((entity) => {
                        return <>{entity}</>;
                      })}
                    </div>

                    {elements.map((entity) => (
                      <div>{entity}</div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PlanSection;
